import { useMemo } from 'react';

import { shallowEqual } from 'react-redux';

import { ContentNugget } from '@backrs_ui/common-ui';

import { ResourcesContent, RESOURCE_KEY } from '@backrs_ui/grow-ui/src/models/models';
import { contentNuggetApi } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { BoxType, InfoBoxConfig } from './InfoBox.config';

type ResourceItem = ContentNugget &
  Readonly<{
    isFeatured?: boolean;
    tab?: string;
  }>;

/**
 * Hook to get sorted resources for a box type
 *
 * Handles:
 * 1. Flattening resources from the box config
 * 2. Extracting sorting metadata (variant, value_rating) from RTK Query cache
 * 3. Sorting by video-internal first, then by value_rating (descending)
 * 4. For SUGGESTED tab: only includes items without isFeatured: true
 * 5. For other tabs: only includes items with isFeatured: true (they get their own tab)
 */
export const useSortedResources = (
  config: InfoBoxConfig | undefined,
  resourcesContent: ResourcesContent | null | undefined,
  boxType?: BoxType,
) => {
  const isSuggestedTab = boxType === 'SUGGESTED';

  // first flatten the resources from the current box config
  const flattenedResources = useMemo(() => {
    if (!config) return [];

    const availableResources = config.resources.filter(
      (resourceKey) => (resourcesContent?.[resourceKey]?.content?.content?.length ?? 0) > 0,
    );

    return (
      availableResources
        .flatMap((resourceKey) => {
          const resourceData = resourcesContent?.[resourceKey];

          if (!resourceData?.content?.content) return [];

          return (resourceData.content.content as ResourceItem[])
            .filter((item) =>
              isSuggestedTab ? item.isFeatured !== true : item.isFeatured === true,
            )
            .map((item) => {
              const relevanceScore =
                resourceData.relevance?.find((scoreObj: Record<string, number>) =>
                  Object.keys(scoreObj).includes(item.id?.toString() || ''),
                )?.[item.id?.toString() || ''] || 0;

              return {
                resourceKey,
                item,
                relevanceScore,
              };
            });
        })
        // deduplicate items that may appear under multiple resource keys
        .filter((entry, index, arr) => arr.findIndex((e) => e.item.id === entry.item.id) === index)
    );
  }, [config, resourcesContent, isSuggestedTab]);

  // then read sorting metadata from rtk query cache for learning/stories resources
  // note that only learning and stories have variant and value_rating for sorting
  const cacheData = useAppSelector(
    (state) =>
      flattenedResources.map(({ resourceKey, item }) => {
        if (resourceKey !== RESOURCE_KEY.LEARNING && resourceKey !== RESOURCE_KEY.STORIES) {
          return null;
        }

        const cacheEntry = contentNuggetApi.endpoints.getContentNuggetById.select(String(item.id))(
          state,
        );

        return cacheEntry?.data || null;
      }),
    shallowEqual,
  );

  // then sort the resources by video-internal first, then by value_rating (desc)
  const sortedContent = useMemo(() => {
    return flattenedResources
      .map((content, index) => {
        const cachedData =
          content.resourceKey === RESOURCE_KEY.LEARNING ||
          content.resourceKey === RESOURCE_KEY.STORIES
            ? cacheData[index]
            : null;

        return {
          ...content,
          variant: cachedData?.variant,
          value_rating: cachedData?.value_rating,
        };
      })
      .sort((a, b) => {
        const aIsInternalVideo = a.variant === 'video-internal';
        const bIsInternalVideo = b.variant === 'video-internal';

        if (aIsInternalVideo && !bIsInternalVideo) return -1;

        if (!aIsInternalVideo && bIsInternalVideo) return 1;

        const aRating = a.value_rating ?? 0;
        const bRating = b.value_rating ?? 0;

        return bRating - aRating;
      });
  }, [flattenedResources, cacheData]);

  return sortedContent;
};
